import { useState, useEffect, useCallback, useRef } from 'react';
import { useGraphQL } from '../../../../apollo/useGraphQL';
import { 
  createKpiMonitoringDashboardResponse,
  createCompanyKPISummary, 
  createBranchStatisticsFromSiteKpiData,
  TRANSMIT_CYCLES,
  TRANSMIT_CYCLE_LABELS,
  TREND_DIRECTIONS
} from '../types';
import { 
  calculateAchievementRate,
  getAggregationPeriod,
  filterDataByDateRange,
  groupKPIDataByCompany,
  calculateBranchStatistics
} from '../utils';

// 지부별 KPI 모니터링 데이터를 가져오는 커스텀 훅
export const useBranchKPIData = (site, period = 'DAY', baseDate = null) => {
  const { executeQuery } = useGraphQL();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    companies: [],
    branchStatistics: null,
    availableKPIs: [],
    lastUpdated: null
  });
  
  // executeQuery 참조를 useRef로 안정적으로 관리
  const executeQueryRef = useRef(executeQuery);
  executeQueryRef.current = executeQuery;

  // 실제 서버 데이터를 가져오는 데이터 로드 함수
  const loadBranchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 기준일을 YYYY-MM-DD 형식으로 변환
      const baseDateStr = baseDate ? 
        (baseDate instanceof Date ? baseDate.toISOString().split('T')[0] : baseDate) 
        : null;

      const query = `
        query GetKpiMonitoringDashboard($period: String, $baseDate: String) {
          getKpiMonitoringDashboard(period: $period, baseDate: $baseDate) {
            siteKpiData {
              site
              siteNm
              kpiData {
                categoryCode
                categoryName
                kpiIndicatorCode
                kpiIndicatorName
                targetValue
                currentValue
                measuredValue
                achievementRate
                topAchievementRate
                bottomAchievementRate
                unit
                description
                participantCompCount
              }
            }
            compKpiData {
              compCd
              compNm
              kpiData {
                categoryCode
                categoryName
                kpiIndicatorCode
                kpiIndicatorName
                targetValue
                currentValue
                measuredValue
                achievementRate
                unit
                transmitCycle
                aggregationDate
                description
                previousPeriodValue
                previousPeriodAchievementRate
                periodComparison
                trendDirection
              }
            }
            lastUpdated
          }
        }
      `;
      
      const result = await executeQueryRef.current(query, { period, baseDate: baseDateStr });
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      const dashboardData = result.data?.getKpiMonitoringDashboard;
      if (!dashboardData) {
        throw new Error('대시보드 데이터를 찾을 수 없습니다.');
      }
      
      // 새로운 데이터 구조로 변환
      const transformedData = transformToDashboardData(dashboardData);
      setData(transformedData);
      
      console.log('KPI 모니터링 데이터 로딩 완료');
    } catch (err) {
      console.error('KPI 모니터링 데이터 로딩 실패:', err);
      setError(err.message);
      // 에러 발생 시 빈 데이터로 설정
      setData({
        companies: [],
        branchStatistics: null,
        availableKPIs: [],
        lastUpdated: null
      });
    } finally {
      setLoading(false);
    }
  }, [period, baseDate]); // baseDate 의존성 추가

  // 서버 데이터를 대시보드 형식으로 변환하는 함수
  const transformToDashboardData = (dashboardData) => {
    console.log('서버에서 받은 전체 데이터:', dashboardData);
    
    const { siteKpiData, compKpiData, lastUpdated } = dashboardData;
    
    // 실제 데이터가 있는지 확인하고 처리
    if (!siteKpiData && !compKpiData) {
      console.warn('siteKpiData와 compKpiData가 모두 비어있습니다.');
      // 데이터가 없을 때 기본 빈 데이터 사용
      return {
        companies: [],
        branchStatistics: {
          site: 'UNKNOWN',
          siteNm: 'Unknown Site',
          indicators: []
        },
        availableKPIs: [],
        lastUpdated: lastUpdated ? new Date(lastUpdated) : new Date(),
        selectedPeriod: period,
        periodLabel: {
          'DAY': '일별',
          'WEEK': '주별',
          'MONTH': '월별'
        }[period] || '일별'
      };
    }
    
    // 사용 가능한 KPI 목록 생성
    const availableKPIs = siteKpiData?.kpiData?.map(kpiDetail => ({
      id: kpiDetail.kpiIndicatorCode,
      name: kpiDetail.kpiIndicatorName,
      categoryId: kpiDetail.categoryCode,
      unit: kpiDetail.unit
    })) || [];
    
    // 회사별 데이터 변환
    const companies = compKpiData?.map(compData => ({
      companyCode: compData.compCd,
      companyName: compData.compNm,
      site: siteKpiData?.site || 'UNKNOWN',
      subscriptions: compData.kpiData?.map(kpiDetail => ({
        kpiIndicatorCode: kpiDetail.kpiIndicatorCode,
        kpiIndicatorName: kpiDetail.kpiIndicatorName,
        categoryId: kpiDetail.categoryCode,
        transmitCycle: kpiDetail.transmitCycle,
        transmitCycleLabel: getTransmitCycleLabel(kpiDetail.transmitCycle),
        targetValue: kpiDetail.targetValue,
        currentValue: kpiDetail.currentValue,
        description: kpiDetail.description,
        kpiData: [{
          measuredValue: kpiDetail.measuredValue,
          targetValue: kpiDetail.targetValue,
          achievementRate: kpiDetail.achievementRate,
          unit: kpiDetail.unit,
          previousPeriodValue: kpiDetail.previousPeriodValue,
          previousPeriodAchievementRate: kpiDetail.previousPeriodAchievementRate,
          periodComparison: kpiDetail.periodComparison,
          trendDirection: kpiDetail.trendDirection
        }]
      })) || []
    })) || [];
    
    // 지부 통계 데이터 변환
    const branchStatistics = createBranchStatisticsFromSiteKpiData(siteKpiData);
    
    return {
      companies,
      branchStatistics,
      availableKPIs,
      lastUpdated: lastUpdated ? new Date(lastUpdated) : new Date(),
      selectedPeriod: period,
      periodLabel: {
        'DAY': '일별',
        'WEEK': '주별',
        'MONTH': '월별'
      }[period] || '일별'
    };
  };
  
  // 전송 주기 라벨 변환 함수
  const getTransmitCycleLabel = (transmitCycle) => {
    const cycleMap = {
      'DAY': '일별',
      'WEEK': '주별',
      'MONTH': '월별',
      'D': '일별',
      'W': '주별',
      'M': '월별'
    };
    return cycleMap[transmitCycle] || transmitCycle;
  };

  // 데이터 새로고침 함수
  const refreshData = useCallback(() => {
    loadBranchData();
  }, [loadBranchData]);

  // 컴포넌트 마운트시 데이터 로드
  useEffect(() => {
    loadBranchData();
  }, [loadBranchData]);

  return {
    ...data,
    loading,
    error,
    refreshData
  };
};

// 특정 회사의 KPI 상세 데이터를 가져오는 커스텀 훅
export const useCompanyKPIDetail = (site, companyCode, kpiIndicatorCode) => {
  const { executeQuery } = useGraphQL();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadCompanyKPIDetail = useCallback(async () => {
    if (!site || !companyCode || !kpiIndicatorCode) return;

    setLoading(true);
    setError(null);

    try {
      // 최근 30일간의 상세 데이터 조회
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 30);

      const query = `
        query GetCompanyKPIDetail($site: String!, $companyCode: String!, $kpiIndicatorCode: String!, $startDate: Date!, $endDate: Date!) {
          companyKPIDetail(
            site: $site, 
            companyCode: $companyCode, 
            kpiIndicatorCode: $kpiIndicatorCode,
            startDate: $startDate,
            endDate: $endDate
          ) {
            aggregationData {
              AGGREGATION_DATE
              MEASURED_VALUE
              ACHIEVEMENT_RATE
              TARGET_VALUE
              DATA_QUALITY_SCORE
            }
            subscription {
              TRANSMIT_CYCLE
              CURRENT_VALUE
              TARGET_VALUE
              DESCRIPTION
            }
          }
        }
      `;

      const result = await executeQuery(query, {
        site,
        companyCode,
        kpiIndicatorCode,
        startDate,
        endDate
      });

      if (result.error) {
        throw new Error(result.error);
      }

      setData(result.data?.companyKPIDetail || null);
    } catch (err) {
      console.error('Error loading company KPI detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [site, companyCode, kpiIndicatorCode, executeQuery]);

  useEffect(() => {
    loadCompanyKPIDetail();
  }, [loadCompanyKPIDetail]);

  return {
    data,
    loading,
    error,
    refresh: loadCompanyKPIDetail
  };
};